import { useEffect, useState, useRef, useCallback } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { CollaboraEditor } from '@/modules/collabora-editor/index.jsx';
import { LiveEditor } from '@/modules/collabora-editor/LiveEditor.jsx';
import { EditorHeader } from '@/modules/live-edit/components/EditorHeader/index.jsx';
import { VersionHistoryPanel } from '@/shared/ui/VersionHistoryPanel.jsx';
import { CommentsDrawer } from '@/modules/my-docs/features/CommentsDrawer.jsx';
import { usePatchRequest, usePostRequest } from '@/shared/hooks/requests.js';
import { FILE, FILE_VERSION_RESTORE } from '@/shared/utils/urls.js';
import { SharingModal } from '@/modules/shared/components/SharingModal.jsx';
import { can } from '@/shared/utils/nodeAccess.js';
import { aipPanelUrl, toggleAssistant } from '@/shared/utils/aipPanel';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import './DocumentEditorModal.css';

/**
 * DocumentEditorModal - Full-screen modal wrapping Collabora Online editor.
 *
 * Replaces the previous TipTap-based editor with Collabora Online,
 * providing true WYSIWYG document editing with native DOCX/PDF support.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {object} doc - The file's row: `uuid`, `name` (and `access` for Share).
 * @param {function} onSave - Optional callback after successful save
 *
 * Real-time collaboration is Collabora's own (callers' `collaborative` prop is
 * ignored).
 *
 * The in-editor assistant dock is armed by CollaboraEditor itself for every
 * edit session (a version preview is read-only, so it never arms).
 */
export function DocumentEditorModal({
  open,
  onClose,
  doc,
  onSave,
}) {
  const { t } = useTranslation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isStarred, setIsStarred] = useState(false);
  const [versionPanelOpen, setVersionPanelOpen] = useState(false);
  // Google-Docs-style version preview: which historical version the editor
  // currently shows read-only (null = the live document), and a local
  // remount counter so a restore reloads the live editor on fresh bytes.
  const [previewVersion, setPreviewVersion] = useState(null);
  const [restoreKey, setRestoreKey] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Local filename so the title bar reflects renames immediately, before
  // the parent re-fetches the file list.
  const [currentFilename, setCurrentFilename] = useState(doc?.name || '');
  const pendingRenameRef = useRef(null);

  const updateFile = usePatchRequest({ url: FILE.replace('{file_uuid}', doc?.uuid) });

  // Reset local filename whenever the doc prop changes (e.g. opening a
  // different file).
  useEffect(() => {
    setCurrentFilename(doc?.name || '');
  }, [doc?.uuid, doc?.name]);

  // Persist a rename via PATCH /file/{file_uuid}. Preserves the original extension if
  // the user removed it from the displayed filename.
  const handleRename = useCallback(async (newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed || trimmed === currentFilename) return;

    // Preserve the file's extension. EditorHeader displays the full filename
    // (e.g. "Q3 report.docx"); if the user deletes the extension we add it
    // back from the original.
    const originalExt = currentFilename.includes('.')
      ? currentFilename.slice(currentFilename.lastIndexOf('.'))
      : '';
    const finalName = trimmed.includes('.') ? trimmed : `${trimmed}${originalExt}`;

    const previous = currentFilename;
    setCurrentFilename(finalName); // optimistic
    const promise = updateFile.request({ data: { name: finalName } });
    pendingRenameRef.current = promise;
    try {
      const { success } = await promise;
      if (success) {
        toast.success(t('editor.renamed', 'File renamed'));
        onSave?.();
      } else {
        setCurrentFilename(previous);
        toast.error(t('editor.renameFailed', 'Failed to rename file'));
      }
    } catch (err) {
      console.error('[DocumentEditorModal] rename failed:', err);
      setCurrentFilename(previous);
      toast.error(t('editor.renameFailed', 'Failed to rename file'));
    } finally {
      pendingRenameRef.current = null;
    }
  }, [currentFilename, updateFile, onSave, t]);

  // Handle editor ready
  const handleEditorReady = useCallback(() => {
    setLastSavedAt(new Date());
  }, []);

  // Handle document modified status from Collabora
  const handleModified = useCallback((modified) => {
    setHasUnsavedChanges(modified);
  }, []);

  // Handle document saved from Collabora
  const handleSaved = useCallback(() => {
    setHasUnsavedChanges(false);
    setLastSavedAt(new Date());
    setIsSaving(false);
    onSave?.();
  }, [onSave]);

  // Handle close — await any in-flight rename so the parent reloads fresh data
  const handleClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        t('editor.unsavedChangesDesc', 'You have unsaved changes. Are you sure you want to close?')
      );
      if (!confirmed) return;
    }
    if (pendingRenameRef.current) {
      await pendingRenameRef.current;
    }
    onClose();
  }, [hasUnsavedChanges, onClose, t]);

  // Handle save via Collabora PostMessage — through the editor's own save(),
  // which addresses the Collabora origin it loaded (never '*').
  const handleSave = useCallback(() => {
    setIsSaving(true);
    const iframe = document.querySelector('iframe[name="collabora-frame"]');
    iframe?._collabora?.save();
    // Fallback: if no save response comes in 5 seconds, reset saving state
    setTimeout(() => setIsSaving(false), 5000);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleSave, handleClose]);

  // A session that went read only has nothing left to save (nor an assistant).
  const [liveMode, setLiveMode] = useState(null);
  const handleLiveState = useCallback(({ mode }) => {
    setLiveMode(mode);
    if (mode === 'view') setHasUnsavedChanges(false);
  }, []);

  const handleVersionRestored = useCallback(() => {
    // Server-side restore already created the new current version — leave
    // preview, remount the live editor so it loads the restored bytes.
    setPreviewVersion(null);
    setRestoreKey((k) => k + 1);
    onSave?.();
  }, [onSave]);

  if (!open || !doc) return null;

  return (
    <div className="doc-editor-modal-overlay">
      <div className="doc-editor-modal">
        {/* Header */}
        <EditorHeader
          filename={currentFilename}
          onFilenameChange={handleRename}
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSavedAt={lastSavedAt}
          onShare={can(doc, 'share') ? () => setShareModalOpen(true) : undefined}
          onShowComments={() => setCommentsOpen(true)}
          onShowHistory={() => setVersionPanelOpen((v) => !v)}
          onToggleAssistant={aipPanelUrl() && previewVersion == null && liveMode === 'edit' ? toggleAssistant : undefined}
          onDownload={() => {
            // Collabora handles downloads via its own UI
            toast.info(t('editor.useCollaboraDownload', 'Use File > Download As in the editor toolbar'));
          }}
          collaborators={[]}
          isStarred={isStarred}
          onToggleStar={() => setIsStarred(!isStarred)}
          onClose={handleClose}
        />

        {/* Main content area */}
        <div className="doc-editor-main-container" style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Collabora Editor (with optional read-only version preview) */}
          <div className="doc-editor-body" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {previewVersion != null && (
              <div className="flex flex-none items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
                <span className="font-medium">
                  {t('editor.previewingVersion', 'Viewing version {{n}} — read-only', { n: previewVersion })}
                </span>
                <RestoreVersionButton
                  fileUuid={doc.uuid}
                  version={previewVersion}
                  onRestored={handleVersionRestored}
                  t={t}
                />
                <button
                  type="button"
                  onClick={() => setPreviewVersion(null)}
                  className="ml-auto rounded border border-amber-300 px-2 py-0.5 font-medium hover:bg-amber-100 cursor-pointer"
                >
                  {t('editor.backToCurrent', 'Back to current')}
                </button>
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {previewVersion != null ? (
                <CollaboraEditor
                  key={`${restoreKey}-${previewVersion}`}
                  fileUuid={doc.uuid}
                  title={currentFilename}
                  versionNumber={previewVersion}
                  onReady={handleEditorReady}
                  onClose={handleClose}
                  onModified={handleModified}
                  onSaved={handleSaved}
                />
              ) : (
                // The live document edits only while the file may be edited:
                // locked, sent for signing, filed… it reopens read only and
                // says why (LiveEditor re-reads the file while open).
                <LiveEditor
                  key={`${restoreKey}-live`}
                  fileUuid={doc.uuid}
                  title={currentFilename}
                  onReady={handleEditorReady}
                  onClose={handleClose}
                  onModified={handleModified}
                  onSaved={handleSaved}
                  onStateChange={handleLiveState}
                />
              )}
            </div>
          </div>
          {/* Version history rail (Google-Docs style) */}
          {versionPanelOpen && (
            <VersionHistoryPanel
              key={restoreKey}
              file={doc}
              previewVersion={previewVersion}
              onPreview={setPreviewVersion}
              onRestored={handleVersionRestored}
              onClose={() => { setVersionPanelOpen(false); setPreviewVersion(null); }}
            />
          )}
        </div>

        {/* Comments Drawer */}
        <CommentsDrawer
          open={commentsOpen}
          onOpenChange={setCommentsOpen}
          file={doc}
        />

        {/* Share Modal */}
        <SharingModal
          openShare={shareModalOpen}
          setOpenShare={setShareModalOpen}
          nodeUuid={doc?.uuid}
          file={doc}
        />
      </div>
    </div>
  );
}

// "Restore this version" action in the preview banner — POSTs the restore
// (server creates a new current version) and hands control back to the host.
function RestoreVersionButton({ fileUuid, version, onRestored, t }) {
  const restoreRequest = usePostRequest({});
  const handleRestore = async () => {
    const { success } = await restoreRequest.request({
      url: FILE_VERSION_RESTORE.replace('{file_uuid}', fileUuid).replace('{version}', version),
    });
    if (success) {
      toast.success(t('editor.versionRestored', 'Version {{n}} restored', { n: version }));
      onRestored?.();
    }
  };
  return (
    <button
      type="button"
      onClick={handleRestore}
      disabled={restoreRequest.loading}
      className="flex items-center gap-1 rounded border border-amber-300 bg-amber-100 px-2 py-0.5 font-semibold hover:bg-amber-200 cursor-pointer disabled:opacity-60"
    >
      {restoreRequest.loading
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <RotateCcw className="h-3 w-3" />}
      {t('editor.restoreThisVersion', 'Restore this version')}
    </button>
  );
}
